import axios from 'axios'
import {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  User,
  EmployeeBasic,
  EmployeePosition,
  Department,
  Evaluation,
  EvaluationRoute,
  EvaluationItem,
  EvaluationItemMaster,
  IndividualEvaluationHistory,
  RankCostMaster,
  SalesMetricsHistory,
  Notification,
  NotificationPreferences,
  Report,
  AnalyticsData,
  DashboardData
} from '../types'

// Axios インスタンス作成（環境変数対応）
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '10000'),
})

// リクエストインターセプター
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// レスポンスインターセプター
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    // 401 Unauthorized または 403 Forbidden の場合
    if (error.response?.status === 401 || error.response?.status === 403) {
      // トークンエラーの場合はlocalStorageをクリア
      if (error.response?.data?.message?.includes('token') ||
          error.response?.data?.message?.includes('expired') ||
          error.response?.data?.message?.includes('Invalid')) {
        console.warn('トークンが無効または期限切れです。ログインページにリダイレクトします。');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.clear();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error)
  }
)

// 認証API
export const authApi = {
  login: (employeeId: string, password: string) =>
    api.post<LoginResponse>('/auth/login', { employee_id: employeeId, password }),

  logout: () =>
    api.post<ApiResponse<null>>('/auth/logout'),

  getCurrentUser: () =>
    api.get<ApiResponse<User>>('/auth/me'),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.put<ApiResponse<null>>('/auth/password', {
      currentPassword,
      newPassword,
    }),

  verifyToken: () =>
    api.get<ApiResponse<{ valid: boolean }>>('/auth/verify'),

  getEvaluatorInfo: () =>
    api.get<
      ApiResponse<{
        is_self_evaluator: boolean
        is_manager_evaluator: boolean
        is_dept_head_evaluator: boolean
        is_goal_approver_evaluator: boolean
      }>
    >('/auth/evaluator-info'),

  getEvaluationPeriodStatus: () =>
    api.get<ApiResponse<{
      hasActivePeriod: boolean;
      period: {
        period_id: string;
        period_name: string;
        status: string;
      } | null;
      canSetGoals: boolean;
      canEditGoals?: boolean;
      canApproveGoals?: boolean;
      canSubmitGoalsForApproval?: boolean;
      goalApprovalStatus?: string | null;
      canSubmitSelfEvaluation: boolean;
      canSubmitManagerEvaluation: boolean;
      canSubmitDeptEvaluation: boolean;
      canSubmitHRReview: boolean;
      currentPhase: string | null;
      systemStatus: string;
    }>>('/evaluation-periods/current/status'),
}

// 評価API (CLAUDE.md設計に基づく)
export const evaluationApi = {
  // 評価データの取得・更新
  getEvaluation: (evaluationId: string) =>
    api.get<ApiResponse<Evaluation>>(`/evaluations/${evaluationId}`),

  createEvaluation: (data: Partial<Evaluation>) =>
    api.post<ApiResponse<Evaluation>>('/evaluations', data),

  updateEvaluation: (evaluationId: string, data: Partial<Evaluation>) =>
    api.put<ApiResponse<Evaluation>>(`/evaluations/${evaluationId}`, data),

  // 評価項目の管理
  getEvaluationItems: (evaluationId: string) =>
    api.get<ApiResponse<EvaluationItem[]>>(`/evaluations/${evaluationId}/items`),

  updateEvaluationItem: (itemDetailId: string, data: Partial<EvaluationItem>) =>
    api.put<ApiResponse<EvaluationItem>>(`/evaluation-items/${itemDetailId}`, data),

  // 個別評価履歴
  submitIndividualEvaluation: (data: Omit<IndividualEvaluationHistory, 'id' | 'created_at' | 'updated_at'>) =>
    api.post<ApiResponse<IndividualEvaluationHistory>>('/individual-evaluations', data),

  getIndividualEvaluationHistory: (evaluationId: string, category?: string) =>
    api.get<ApiResponse<IndividualEvaluationHistory[]>>(`/individual-evaluations`, {
      params: { evaluation_id: evaluationId, category }
    }),

  // 技術部門の定量計算
  calculateTechQuantitative: (employeeId: string, annualSales: number, teamData: {
    teamMemberCount: number;
    domesticCount: number;
    offshoreCount: number;
  }) =>
    api.post<ApiResponse<{ dpValue: number; dpScore: number; rankCost: number }>>('/evaluations/calculate/tech-quantitative', {
      employee_id: employeeId,
      annual_sales: annualSales,
      ...teamData
    }),

  // 営業部門の定量計算
  calculateSalesQuantitative: (employeeId: string, evaluationPeriod: string) =>
    api.post<ApiResponse<{ quantitativeScore: number; achievements: any }>>('/evaluations/calculate/sales-quantitative', {
      employee_id: employeeId,
      evaluation_period: evaluationPeriod
    }),

  // 昇給率計算
  calculateSalaryIncrease: (evaluationId: string) =>
    api.post<ApiResponse<{ salaryIncreaseRate: number; performanceScore: number }>>(`/evaluations/${evaluationId}/calculate-salary`),
}

// 評価フローAPI
export const evaluationFlowApi = {
  getCurrentPhase: () =>
    api.get<ApiResponse<{ currentPhase: string; canProgress: boolean }>>('/evaluations/flow/current-phase'),

  getDashboard: () =>
    api.get<ApiResponse<DashboardData>>('/evaluations/flow/dashboard'),

  getProgressSummary: () =>
    api.get<ApiResponse<any>>('/evaluations/flow/progress-summary'),

  forceProgress: (evaluationId: number, fromPhase: string, toPhase: string, reason: string) =>
    api.post<ApiResponse<null>>('/evaluations/flow/force-progress', {
      evaluation_id: evaluationId,
      from_phase: fromPhase,
      to_phase: toPhase,
      reason,
    }),
}

// 通知API
export const notificationApi = {
  getNotifications: (limit?: number, offset?: number) =>
    api.get<ApiResponse<{ notifications: Notification[]; total: number }>>('/notifications', {
      params: { limit, offset },
    }),

  getUnreadCount: () =>
    api.get<ApiResponse<{ count: number }>>('/notifications/unread-count'),

  markAsRead: (id: number) =>
    api.patch<ApiResponse<null>>(`/notifications/${id}/read`),

  markMultipleAsRead: (ids: number[]) =>
    api.patch<ApiResponse<null>>('/notifications/read-multiple', { ids }),

  markAllAsRead: () =>
    api.patch<ApiResponse<null>>('/notifications/read-all'),

  getPreferences: () =>
    api.get<ApiResponse<NotificationPreferences>>('/notifications/preferences'),

  updatePreferences: (preferences: NotificationPreferences) =>
    api.put<ApiResponse<null>>('/notifications/preferences', preferences),

  getStatistics: () =>
    api.get<ApiResponse<any>>('/notifications/statistics'),

  sendEvaluationReminder: (phase: string, daysRemaining: number, targetUsers: string[]) =>
    api.post<ApiResponse<null>>('/notifications/evaluation-reminder', {
      phase,
      days_remaining: daysRemaining,
      target_users: targetUsers,
    }),

  testEmail: (email: string) =>
    api.post<ApiResponse<null>>('/notifications/test-email', { email }),
}

// レポートAPI
export const reportApi = {
  getAnalytics: (year?: number) =>
    api.get<ApiResponse<AnalyticsData>>('/reports/analytics', {
      params: { year },
    }),

  getAdvancedAnalytics: (year: number) =>
    api.get<ApiResponse<AnalyticsData>>(`/reports/advanced-analytics?year=${year}`),

  generateIndividual: (userId: number, year: number, format: 'pdf' | 'excel') =>
    api.post<ApiResponse<{ report_id: number }>>('/reports/individual', {
      user_id: userId,
      evaluation_year: year,
      file_format: format,
    }),

  generateDepartment: (department: string, year: number, format: 'pdf' | 'excel') =>
    api.post<ApiResponse<{ report_id: number }>>('/reports/department', {
      department,
      evaluation_year: year,
      file_format: format,
    }),

  generateCompany: (year: number, format: 'pdf' | 'excel') =>
    api.post<ApiResponse<{ report_id: number }>>('/reports/company', {
      evaluation_year: year,
      file_format: format,
    }),

  getStatus: (reportId: number) =>
    api.get<ApiResponse<Report>>(`/reports/status/${reportId}`),

  downloadReport: (reportId: number) =>
    api.get(`/reports/download/${reportId}`, {
      responseType: 'blob',
    }),

  getHistory: (limit?: number, offset?: number) =>
    api.get<ApiResponse<Report[]>>('/reports/history', {
      params: { limit, offset },
    }),

  exportCSV: (reportType: string, parameters: any) =>
    api.post('/reports/export/csv', {
      report_type: reportType,
      parameters,
    }, {
      responseType: 'blob',
    }),
}

// 社員検索結果型
export interface EmployeeSearchResult {
  employee_id: string;
  name: string;
  department_name?: string;
  department_type?: string;
  rank?: string;
}

// 従業員管理API (CLAUDE.md設計に基づく)
export const employeeApi = {
  // 社員検索（Autocomplete用）
  searchEmployees: async (query: string, options?: { department_type?: string; limit?: number }): Promise<EmployeeSearchResult[]> => {
    const response = await api.get<ApiResponse<{ employees: EmployeeSearchResult[]; hasMore: boolean }>>('/employees/search', {
      params: { q: query, department_type: options?.department_type, limit: options?.limit || 50 }
    });
    return response.data.data.employees;
  },

  // 基本社員情報
  getEmployeeBasic: (employeeId: string) =>
    api.get<ApiResponse<EmployeeBasic>>(`/employees/basic/${employeeId}`),

  createEmployeeBasic: (data: Omit<EmployeeBasic, 'created_at' | 'updated_at'>) =>
    api.post<ApiResponse<EmployeeBasic>>('/employees/basic', data),

  updateEmployeeBasic: (employeeId: string, data: Partial<EmployeeBasic>) =>
    api.put<ApiResponse<EmployeeBasic>>(`/employees/basic/${employeeId}`, data),

  // 所属・職位情報
  getEmployeePosition: (employeeId: string, current: boolean = true) =>
    api.get<ApiResponse<EmployeePosition[]>>(`/employees/position/${employeeId}`, {
      params: { current }
    }),

  createEmployeePosition: (data: Omit<EmployeePosition, 'id' | 'created_at' | 'updated_at'>) =>
    api.post<ApiResponse<EmployeePosition>>('/employees/position', data),

  updateEmployeePosition: (positionId: number, data: Partial<EmployeePosition>) =>
    api.put<ApiResponse<EmployeePosition>>(`/employees/position/${positionId}`, data),

  // 複合データ（基本情報+現在の職位）
  getEmployees: (limit?: number, offset?: number, department?: string) =>
    api.get<ApiResponse<{ data: User[]; total: number; limit: number; offset: number }>>('/employees', {
      params: { limit, offset, department },
    }),

  getEmployee: (employeeId: string) =>
    api.get<ApiResponse<User>>(`/employees/${employeeId}`),
}

// 評価ルート管理API (HR専用)
export const evaluationRouteApi = {
  getRoutes: (evaluationPeriod: string, employeeId?: string) =>
    api.get<ApiResponse<EvaluationRoute[]>>('/evaluation-routes', {
      params: { evaluation_period: evaluationPeriod, employee_id: employeeId }
    }),

  createRoute: (data: Omit<EvaluationRoute, 'id' | 'created_at' | 'updated_at'>) =>
    api.post<ApiResponse<EvaluationRoute>>('/evaluation-routes', data),

  updateRoute: (routeId: number, data: Partial<EvaluationRoute>) =>
    api.put<ApiResponse<EvaluationRoute>>(`/evaluation-routes/${routeId}`, data),

  deleteRoute: (routeId: number) =>
    api.delete<ApiResponse<null>>(`/evaluation-routes/${routeId}`),

  // 評価期間の調整率一括更新
  updateCompanyAdjustmentRate: (evaluationPeriod: string, adjustmentRate: number) =>
    api.put<ApiResponse<null>>('/evaluation-routes/company-adjustment-rate', {
      evaluation_period: evaluationPeriod,
      company_adjustment_rate: adjustmentRate
    }),
}

// ランクコスト管理API (HR専用)
export const rankCostApi = {
  getRankCosts: () =>
    api.get<ApiResponse<RankCostMaster[]>>('/rank-costs'),

  updateRankCost: (rank: string, data: Partial<RankCostMaster>) =>
    api.put<ApiResponse<RankCostMaster>>(`/rank-costs/${rank}`, data),
}

// 営業指標管理API
export const salesMetricsApi = {
  getMetrics: (employeeId: string, evaluationPeriod: string) =>
    api.get<ApiResponse<SalesMetricsHistory[]>>('/sales-metrics', {
      params: { employee_id: employeeId, evaluation_period: evaluationPeriod }
    }),

  createMetrics: (data: Omit<SalesMetricsHistory, 'id' | 'created_at' | 'updated_at'>) =>
    api.post<ApiResponse<SalesMetricsHistory>>('/sales-metrics', data),

  updateMetrics: (metricsId: number, data: Partial<SalesMetricsHistory>) =>
    api.put<ApiResponse<SalesMetricsHistory>>(`/sales-metrics/${metricsId}`, data),

  // 部門長による営業目標・実績入力
  bulkUpdateMetrics: (evaluationPeriod: string, metrics: Array<{
    employee_id: string;
    target_expected_order?: number;
    target_sales?: number;
    target_gross_profit?: number;
    actual_expected_order?: number;
    actual_sales?: number;
    actual_gross_profit?: number;
  }>) =>
    api.post<ApiResponse<null>>('/sales-metrics/bulk-update', {
      evaluation_period: evaluationPeriod,
      metrics
    }),
}

// ユーザー管理API (互換性のため)
export const userApi = {
  getUsers: (limit?: number, offset?: number, department?: string) =>
    employeeApi.getEmployees(limit, offset, department),

  getUser: (employeeId: string) =>
    employeeApi.getEmployee(employeeId),

  getUserById: (employeeId: string) =>
    api.get<ApiResponse<User>>(`/employees/basic/${employeeId}`),

  createUser: (userData: any) =>
    employeeApi.createEmployeeBasic(userData),

  updateUser: (id: string, userData: any) =>
    api.put<ApiResponse<any>>(`/employees/basic/${id}`, userData),

  deactivateUser: (id: number) =>
    api.patch<ApiResponse<null>>(`/employees/${id}/deactivate`),

  resetPassword: (employeeId: string) =>
    api.post<ApiResponse<{ temporaryPassword: string }>>(`/auth/reset-password`, { employee_id: employeeId }),
}

// 部門マスタAPI
export const departmentApi = {
  getDepartments: () =>
    api.get<ApiResponse<Department[]>>('/departments'),

  getDepartment: (departmentId: string) =>
    api.get<ApiResponse<Department>>(`/departments/${departmentId}`),
}

// 評価期間マスタAPI (HR専用)
export const evaluationPeriodApi = {
  getEvaluationPeriods: (status?: string) =>
    api.get<ApiResponse<any[]>>('/evaluation-periods', {
      params: status ? { status } : {}
    }),

  getEvaluationPeriod: (periodId: string) =>
    api.get<ApiResponse<any>>(`/evaluation-periods/${periodId}`),

  createEvaluationPeriod: (data: {
    period_id: string;
    period_name: string;
    self_eval_start: string;
    self_eval_end: string;
    manager_eval_start: string;
    manager_eval_end: string;
    dept_eval_start: string;
    dept_eval_end: string;
    hr_review_start: string;
    hr_review_end: string;
    company_adjustment_rate: number;
    status?: 'draft' | 'active' | 'closed';
  }) =>
    api.post<ApiResponse<any>>('/evaluation-periods', data),

  updateEvaluationPeriod: (periodId: string, data: Partial<{
    period_name: string;
    self_eval_start: string;
    self_eval_end: string;
    manager_eval_start: string;
    manager_eval_end: string;
    dept_eval_start: string;
    dept_eval_end: string;
    hr_review_start: string;
    hr_review_end: string;
    company_adjustment_rate: number;
    status: 'draft' | 'active' | 'closed';
  }>) =>
    api.put<ApiResponse<any>>(`/evaluation-periods/${periodId}`, data),

  deleteEvaluationPeriod: (periodId: string) =>
    api.delete<ApiResponse<null>>(`/evaluation-periods/${periodId}`),
}

// レポートAPI
export const reportsApi = {
  // 評価期間一覧を取得
  getEvaluationPeriods: () =>
    api.get<ApiResponse<Array<{ period_id: string; period_name: string }>>>('/reports/evaluation-periods'),

  // 評価分布統計を取得
  getEvaluationDistribution: (period: string) =>
    api.get<ApiResponse<{
      distribution: Array<{
        department_type: string;
        department_name: string;
        job_grade: string;
        employee_count: number;
        avg_performance: number;
        min_performance: number;
        max_performance: number;
        avg_salary_increase: number;
      }>;
      scoreDistribution: Array<{
        score_range: number;
        count: number;
      }>;
    }>>('/reports/evaluation-distribution', {
      params: { period }
    }),

  // パフォーマンス推移を取得（自分の全履歴）
  getPerformanceTrends: () =>
    api.get<ApiResponse<Array<{
      evaluation_period: string;
      employee_id: string;
      name: string;
      performance_score: number;
      salary_increase_rate: number;
      updated_at: string;
    }>>>('/reports/performance-trends'),

  // 昇給率シミュレーションを実行
  getSalaryIncreaseSimulation: (params: {
    performanceScore: number;
    yearsOfService: number;
    companyAdjustmentRate: number;
  }) =>
    api.get<ApiResponse<{
      performanceScore: number;
      yearsOfService: number;
      companyAdjustmentRate: number;
      calculatedIncreaseRate: number;
      breakdown: {
        performanceComponent: number;
        serviceYearsComponent: number;
        companyAdjustmentComponent: number;
      };
    }>>('/reports/salary-increase-simulation', {
      params
    }),

  // データエクスポート（CSV/JSON）
  exportData: (period: string, format: 'csv' | 'json') =>
    api.get(`/reports/export`, {
      params: { period, format },
      responseType: format === 'csv' ? 'blob' : 'json'
    }),

  // 自分の評価進捗を取得（読み取り専用）
  getMyEvaluationProgress: (period_id?: string) =>
    api.get<ApiResponse<{
      goal_approval_status?: string | null;
      employee: any;
      evaluation: any;
      items: any[];
      salesMetrics: any;
      evaluators: any[];
    }>>('/reports/my-evaluation-progress', {
      params: { period_id }
    }),

  // 評価者として担当している従業員一覧を取得
  getEvaluatorAssignments: (period_id: string, evaluation_category?: string) =>
    api.get<ApiResponse<Array<{
      employee_id: string;
      name: string;
      department_name: string;
      job_grade: string;
      rank: string;
      status: string;
      goal_approval_status?: string | null;
      performance_score: number | null;
      salary_increase_rate: number | null;
      evaluation_category: string;
      evaluation_weight: number;
    }>>>('/reports/evaluator-assignments', {
      params: { period_id, evaluation_category }
    }),

  // 評価者として担当している従業員の統計分析を取得
  getEvaluatorAnalytics: (period_id: string) =>
    api.get<ApiResponse<{
      totalCount: number;
      performanceScore: {
        min: number | null;
        max: number | null;
        avg: number | null;
        scoredCount?: number;
      };
      salaryIncreaseRate: {
        min: number | null;
        max: number | null;
        avg: number | null;
        scoredCount?: number;
      };
      statusDistribution: Array<{
        status: string;
        count: number;
      }>;
      departmentDistribution: Array<{
        department_name: string;
        count: number;
        avg_performance_score: number | null;
      }>;
    }>>('/reports/evaluator-analytics', {
      params: { period_id }
    }),

  // 特定従業員の評価履歴を取得（過去5期間）
  getEmployeeHistory: (employeeId: string, limit?: number) =>
    api.get<ApiResponse<Array<{
      evaluation_period: string;
      performance_score: number | null;
      salary_increase_rate: number | null;
      status: string;
      updated_at: string;
    }>>>(`/reports/employee-history/${employeeId}`, {
      params: { limit }
    }),

  // 評価詳細を取得（自分の評価または担当している評価）
  getEvaluationDetail: (employeeId: string, periodId: string) =>
    api.get<ApiResponse<{
      employee: any;
      evaluation: any;
      evaluation_items: any[];
      sales_metrics: any;
      evaluators: any[];
    }>>(`/reports/evaluation-detail/${employeeId}/${periodId}`),

  // 全社評価進捗（HR向け）: 段階別ファネル＋部門別完了率
  getOrgProgress: (period_id?: string) =>
    api.get<ApiResponse<{
      period: { period_id: string; period_name: string; status: string } | null;
      periods: Array<{ period_id: string; period_name: string; status: string }>;
      funnel: {
        total: number;
        not_started: number;
        root_defined: number;
        goal_setting: number;
        self_evaluated: number;
        manager_evaluated: number;
        dept_evaluated: number;
        r_applied: number;
        finalized: number;
      } | null;
      departmentDistribution: Array<{
        department_name: string;
        total: number;
        completed: number;
        completion_rate: number;
      }>;
      departmentMatrix: Array<{
        department_name: string;
        total: number;
        stages: {
          not_started: number;
          goal_setting: number;
          self_evaluated: number;
          manager_evaluated: number;
          dept_evaluated: number;
          r_applied: number;
          finalized: number;
        };
        progress_index: number;
        completed: number;
        completion_rate: number;
        stalled_count: number;
      }>;
      stallSummary: {
        thresholdDays: number;
        totalStalled: number;
        topStage: { key: string; label: string; count: number; stalled: number; avgDays: number | null } | null;
        byStage: Array<{ key: string; label: string; count: number; stalled: number; avgDays: number | null }>;
      };
      nextActionOwners: {
        evaluators: Array<{
          evaluator_id: string;
          evaluator_name: string;
          stage: 'manager' | 'dept_head';
          pending_count: number;
        }>;
        selfPending: number;
        hrPending: number;
      };
    }>>('/reports/org-progress', {
      params: period_id ? { period_id } : {}
    }),
}

// プロフィールAPI
export const profileApi = {
  // プロフィール情報を取得
  getProfile: () =>
    api.get<ApiResponse<{
      basic: EmployeeBasic;
      currentPosition: EmployeePosition & {
        department_name: string;
        department_type: string;
        rank_cost: number;
      };
      positionHistory: Array<EmployeePosition & {
        department_name: string;
      }>;
      evaluationHistory: Array<{
        evaluation_id: string;
        evaluation_period: string;
        performance_score: number;
        salary_increase_rate: number;
        status: string;
        updated_at: string;
      }>;
    }>>('/profile'),

  // プロフィール情報を更新（メールアドレスのみ）
  updateProfile: (email: string) =>
    api.put<ApiResponse<null>>('/profile', { email }),

  // パスワード変更
  changePassword: (data: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) =>
    api.put<ApiResponse<null>>('/profile/change-password', data),
}

export default api